'use client';

import { ArrowRight, Code2, Sparkles, ShieldCheck, HeartHandshake } from 'lucide-react';
import { motion, useReducedMotion } from 'framer-motion';
import { useScrollReveal } from '@/app/hooks/use-scroll-reveal';

const SERVICES = [
  {
    icon: Code2,
    title: 'Engineering',
    description:
      'Modern web and mobile platforms built on Next.js, React, and TypeScript. Production-ready from day one.',
    href: '/contact',
  },
  {
    icon: Sparkles,
    title: 'Design',
    description:
      'Accessible, on-brand interfaces. Tested with real users before they ship.',
    href: '/contact',
  },
  {
    icon: ShieldCheck,
    title: 'Operate & support',
    description:
      "We don't hand off and walk away. We run, observe, and improve our products continuously.",
    href: '/contact',
  },
  {
    icon: HeartHandshake,
    title: 'Partnerships',
    description:
      'We partner with mission-aligned organisations to build digital products that serve communities.',
    href: '/contact',
  },
] as const;

export function WhatWeDo() {
  const { ref, isVisible } = useScrollReveal();
  const prefersReducedMotion = useReducedMotion();

  return (
    <section id="services" className="py-[clamp(80px,10vw,140px)]">
      <div
        ref={ref}
        className="mx-auto max-w-[1200px] px-5 md:px-10 lg:px-0"
      >
        {/* Section header */}
        <div className="mb-16 max-w-[720px]">
          <div className="flex items-center gap-3">
            <span className="h-px w-6 bg-sandron-ink-300" aria-hidden="true" />
            <span className="font-body text-xs font-semibold uppercase tracking-[0.12em] text-sandron-ink-500">
              What we do
            </span>
          </div>
          <h2 className="mt-4 font-display text-[clamp(32px,4vw,56px)] font-bold leading-[1.1] tracking-[-0.02em] text-sandron-ink-900">
            A studio, not an agency.
          </h2>
          <p className="mt-4 max-w-[600px] text-lg leading-[1.55] text-sandron-ink-700">
            We design and operate our own products. The work below is how we get
            there — and how we partner with mission-aligned organisations on
            theirs.
          </p>
        </div>

        {/* Card grid */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 lg:gap-7">
          {SERVICES.map((service, index) => (
            <motion.a
              key={service.title}
              href={service.href}
              initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 24 }}
              animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 24 }}
              transition={{
                duration: prefersReducedMotion ? 0.15 : 0.6,
                delay: index * 0.09,
                ease: [0.22, 1, 0.36, 1] as [number, number, number, number],
              }}
              className="group flex min-h-[280px] flex-col rounded-[20px] border border-sandron-ink-100 bg-white p-6 transition-all duration-[250ms] lg:min-h-[320px] lg:p-8 [@media(hover:hover)]:hover:-translate-y-1 [@media(hover:hover)]:hover:border-sandron-ink-200 [@media(hover:hover)]:hover:shadow-[0_16px_40px_-16px_rgba(11,18,32,0.18)] motion-reduce:!transform-none"
              aria-label={`${service.title} — learn more`}
            >
              {/* Icon */}
              <div className="flex h-14 w-14 items-center justify-center rounded-[14px] bg-sandron-ink-50 text-sandron-ink-900 transition-colors duration-200 group-hover:bg-[rgba(31,107,68,0.12)] group-hover:text-spacenest-primary">
                <service.icon size={28} strokeWidth={2} />
              </div>

              {/* Content */}
              <div className="mt-auto pt-6">
                <h3 className="font-display text-[22px] font-bold leading-[1.2] text-sandron-ink-900">
                  {service.title}
                </h3>
                <p className="mt-2.5 text-[15px] leading-[1.6] text-sandron-ink-700">
                  {service.description}
                </p>
              </div>

              {/* Footer link */}
              <div className="mt-4 flex items-center gap-1.5 text-sm font-medium text-sandron-ink-900">
                Learn more
                <ArrowRight
                  size={14}
                  className="transition-transform duration-200 group-hover:translate-x-1"
                />
              </div>
            </motion.a>
          ))}
        </div>
      </div>
    </section>
  );
}
